import { Command, CommandsRegistry } from "../../../../platform/commands/common/commands";
import { ServicesAccessor } from "../../../../platform/instantiation/common/instantiation";
import { IAssetsService, AssetType, Asset, AssetFetchOptions, FullAsset, assetTypeDisplayName, AssetTypes } from "./assets";
import { CancellationToken } from "../../../../base/common/cancellation";
import { safeStringify } from "../../../../base/common/objects";
import { ILogService } from "../../../../platform/log/common/log";

// #region Asset Query Commands

/**
 * Arguments for querying available asset options
 */
interface QueryAssetOptionsArguments {
    // No arguments needed
}

/**
 * Command to query available asset types, categories, and tags.
 * Returns information about what types of assets are available and how they can be filtered.
 *
 * Example usage:
 * ```typescript
 * const options = await executeCommand('assets.queryOptions');
 * console.log(options.types); // ['texture', 'model', 'hdr', 'material']
 * console.log(options.categories); // { texture: ['wood', 'metal', ...] }
 * ```
 */
export class QueryAssetOptionsCommand extends Command<QueryAssetOptionsArguments> {
    static readonly ID = "assets.read-options";

    constructor() {
        super(QueryAssetOptionsCommand.ID, "Query Asset Options", {
            description: "Query available asset types, categories, and tags from the asset library",
            args: {
                name: 'args',
                description: 'No arguments required',
                schema: {
                    type: 'object',
                    properties: {}
                }
            },
            returns: 'Object with types (array), categories (object), and tags (object)'
        });
    }

    async execute(accessor: ServicesAccessor): Promise<unknown> {
        const assetsService = accessor.get(IAssetsService);
        return await assetsService.queryOptions();
    }

    async executeForAI(accessor: ServicesAccessor): Promise<string> {
        const result = await this.execute(accessor);
        return safeStringify(result);
    }
}

/**
 * Arguments for searching assets
 */
interface SearchAssetsArguments {
    /**
     * The type of assets to search for
     * Valid values: "texture", "model", "hdr", "material"
     */
    type: AssetType;

    /**
     * Optional text query to filter assets by name or description
     */
    query?: string;

    /**
     * Optional categories to filter by (e.g., ["wood", "metal"])
     */
    category?: string[];

    /**
     * Optional tags to filter by (e.g., ["pbr", "seamless"])
     */
    tags?: string[];
}

/**
 * Command to search for assets in the asset library.
 * Returns a list of assets matching the search criteria.
 *
 * Example usage:
 * ```typescript
 * // Search for all textures
 * const result = await executeCommand('assets.search', { type: 'texture' });
 *
 * // Search for wood textures
 * const result = await executeCommand('assets.search', {
 *   type: 'texture',
 *   query: 'wood'
 * });
 *
 * // Search for PBR materials with specific tags
 * const result = await executeCommand('assets.search', {
 *   type: 'material',
 *   tags: ['pbr', 'seamless']
 * });
 * ```
 */
export class SearchAssetsCommand extends Command<SearchAssetsArguments> {
    static readonly ID = "assets.search-asset";

    constructor() {
        super(SearchAssetsCommand.ID, "Search Assets", {
            description: "Search for assets in the asset library",
            args: {
                name: 'args',
                description: 'Search criteria for finding assets',
                schema: {
                    type: 'object',
                    description: 'Asset search parameters',
                    properties: {
                        type: {
                            type: 'string',
                            description: 'The type of assets to search for',
                            enum: [...AssetTypes],
                        },
                        query: {
                            type: 'string',
                            description: 'Optional text query to filter assets by name, description, or tags'
                        },
                        category: {
                            type: 'array',
                            description: 'Optional categories to filter by (e.g., ["wood", "metal"])',
                            items: {
                                type: 'string'
                            }
                        },
                        tags: {
                            type: 'array',
                            description: 'Optional tags to filter by (e.g., ["pbr", "seamless"])',
                            items: {
                                type: 'string'
                            }
                        }
                    },
                    required: ['type']
                }
            },
            returns: 'Object with assets array containing matching assets'
        });
    }

    async execute(accessor: ServicesAccessor, args: SearchAssetsArguments): Promise<{ assets: Asset[] }> {
        const assetsService = accessor.get(IAssetsService);
        const logger = accessor.get(ILogService);

        if (!args.type) {
            throw new Error("'type' is required. Valid values: 'texture', 'model', 'hdr', 'material'");
        }

        const options: AssetFetchOptions = {
            type: args.type,
            category: args.category,
            tags: args.tags,
        };

        logger.info(`Executing asset search with options:`, options);
        const result = await assetsService.list(options, CancellationToken.None);
        logger.info(`Asset search returned ${result.assets.length} assets`);

        // Filter by query if provided
        if (args.query?.length && result.assets.length > 0) {
            const queryLower = args.query.toLowerCase();
            result.assets = result.assets.filter(asset => {
                const name = asset.name?.toLowerCase() || '';
                const description = asset.description?.toLowerCase() || '';
                const tags = asset.tags?.join(' ').toLowerCase() || '';
                return name.includes(queryLower) ||
                    description.includes(queryLower) ||
                    tags.includes(queryLower);
            });
        }

        return result;
    }

    async executeForAI(accessor: ServicesAccessor, args: SearchAssetsArguments): Promise<string> {
        const result = await this.execute(accessor, args);

        if (result.assets.length === 0) {
            return `No ${assetTypeDisplayName(args.type)} assets found.`;
        }

        const summary = result.assets.map(asset => {
            let info = `- ID: ${asset.id}`;
            if (asset.name) info += `, Name: ${asset.name}`;
            if (asset.description) info += `, Description: ${asset.description}`;
            if (asset.category) info += `, Category: ${asset.category}`;
            if (asset.tags && asset.tags.length > 0) info += `, Tags: ${asset.tags.join(', ')}`;
            return info;
        }).join('\n');

        return `Found ${result.assets.length} ${assetTypeDisplayName(args.type)} asset(s):\n${summary}`;
    }
}

/**
 * Arguments for getting asset details
 */
interface GetAssetDetailsArguments {
    /**
     * The asset ID to load details for
     */
    id: string;
}

/**
 * Command to get full details about a specific asset by its ID.
 * Returns detailed information including all available versions, resolutions, and download URLs.
 *
 * Example usage:
 * ```typescript
 * // First, search for assets to get their IDs
 * const searchResult = await executeCommand('assets.search', { type: 'texture' });
 * const assetId = searchResult.assets[0].id;
 *
 * // Then get full details
 * const details = await executeCommand('assets.getDetails', { id: assetId });
 * console.log(details.versions); // Array of available versions with URIs, resolutions, etc.
 * ```
 */
export class GetAssetDetailsCommand extends Command<GetAssetDetailsArguments> {
    static readonly ID = "assets.read-details";

    constructor() {
        super(GetAssetDetailsCommand.ID, "Get Asset Details", {
            description: "Get full details about a specific asset by its ID",
            args: {
                name: 'args',
                description: 'Asset identifier to retrieve details for',
                schema: {
                    type: 'object',
                    description: 'Parameters for retrieving asset details',
                    properties: {
                        id: {
                            type: 'string',
                            description: 'The asset ID from search results'
                        }
                    },
                    required: ['id']
                }
            },
            returns: 'Object with id (string) and versions (array of available versions with uri, resolution, format, fileSize)'
        });
    }

    async execute(accessor: ServicesAccessor, args: GetAssetDetailsArguments): Promise<FullAsset | null> {
        const assetsService = accessor.get(IAssetsService);

        if (!args.id) {
            throw new Error("'id' is required");
        }

        return await assetsService.loadAsset(args.id, CancellationToken.None);
    }

    async executeForAI(accessor: ServicesAccessor, args: GetAssetDetailsArguments): Promise<string> {
        const result = await this.execute(accessor, args);

        if (!result) {
            return `Asset with ID '${args.id}' not found.`;
        }

        let info = `Asset ID: ${result.id}\n`;
        info += `Available versions: ${result.versions.length}\n`;

        result.versions.forEach((version, index) => {
            info += `\nVersion ${index + 1}:`;
            if (version.version) info += `\n  - Version: ${version.version}`;
            if (version.format) info += `\n  - Format: ${version.format}`;
            if (version.resolution) {
                if (typeof version.resolution === 'object') {
                    info += `\n  - Resolution: ${version.resolution.width}x${version.resolution.height}`;
                } else {
                    info += `\n  - Resolution: ${version.resolution}`;
                }
            }
            if (version.fileSize) info += `\n  - File Size: ${(version.fileSize / 1024 / 1024).toFixed(2)} MB`;
            if (version.uri) info += `\n  - URI: ${version.uri.toString()}`;
        });

        return info;
    }
}

// #endregion

// #region Command Registration

// Register all commands
CommandsRegistry.registerCommand(new QueryAssetOptionsCommand());
CommandsRegistry.registerCommand(new SearchAssetsCommand());
CommandsRegistry.registerCommand(new GetAssetDetailsCommand());

// #endregion

